/**
 * Lesson A/B tests (#5230): experiments defined in data, on the lesson doc,
 * rather than in the code registry. Staff set one up in the lesson editor by
 * naming two or more versions and choosing, per slide, whether everyone sees
 * it or only one version's students do. Each version is an arm; the reward is
 * completing the lesson within the window after first exposure.
 *
 * The webapp (assignment, slide filtering, the editor's results panel) and the
 * `refreshbanditposterior` function both build the experiment definition here,
 * so the two can never disagree about a lesson test's structure.
 */

import {z} from 'zod';

import {
  DEFAULT_COHORT,
  type BanditExperimentDefinition,
  type BetaParams,
} from './types';
import {buildArmOverrideParam} from './urlOverride';

/** Name of the lesson doc field that holds a lesson's A/B test. */
export const LESSON_AB_TEST_FIELD = 'abTest';

/** Most versions one lesson test may have. */
export const MAX_LESSON_TEST_VERSIONS = 4;

/**
 * Reward window for lesson tests, in hours: a student counts as a success
 * when they complete the lesson within this long after first reaching a
 * version-specific slide.
 */
export const LESSON_TEST_REWARD_WINDOW_HOURS = 168;

/** Uniform prior on every version's completion rate. */
export const LESSON_TEST_PRIOR: BetaParams = {alpha: 1, beta: 1};

/** Prefix of every lesson test's experiment ID. */
export const LESSON_EXPERIMENT_ID_PREFIX = 'lesson-';

/** Longest experiment ID the refresh function accepts. */
const MAX_EXPERIMENT_ID_LENGTH = 64;

/** One version of a lesson test. */
export const lessonTestVersionSchema = z.object({
  /** Stable arm name (`a`, `b`, …); renaming the version never changes it. */
  id: z.string().min(1).max(8),
  /** Display name shown to staff, e.g. the presenter's name. */
  name: z.string().max(60),
});

/** One version of a lesson test. See {@link lessonTestVersionSchema}. */
export type LessonTestVersion = z.infer<typeof lessonTestVersionSchema>;

/** The `abTest` field of a lesson doc. */
export const lessonAbTestSchema = z.object({
  /**
   * Bandit experiment ID, fixed when the test is first set up. Events live
   * under `bandits/{courseId}/experiments/{experimentId}`.
   */
  experimentId: z
    .string()
    .startsWith(LESSON_EXPERIMENT_ID_PREFIX)
    .max(MAX_EXPERIMENT_ID_LENGTH),
  /** Whether students are being assigned. Off → everyone sees the first version. */
  enabled: z.boolean(),
  /** The versions, in the order staff see them. The first is the default. */
  versions: z.array(lessonTestVersionSchema).max(MAX_LESSON_TEST_VERSIONS),
  /**
   * Slide id → version id for slides only one version's students see. A slide
   * that is not listed (or lists a version that no longer exists) is shown to
   * everyone.
   */
  slideVersions: z.record(z.string(), z.string()).default({}),
});

/** The `abTest` field of a lesson doc. See {@link lessonAbTestSchema}. */
export type LessonAbTest = z.infer<typeof lessonAbTestSchema>;

/**
 * Reads a lesson doc's `abTest` field.
 *
 * @param raw - The stored field value; undefined when the lesson never had a test.
 * @return The parsed test, or null when absent or malformed.
 * @throws Never.
 */
export function parseLessonAbTest(raw: unknown): LessonAbTest | null {
  if (raw === undefined) {
    return null;
  }
  const parsed = lessonAbTestSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Builds a fresh experiment ID for a lesson test, dated so that a later test
 * on the same lesson never reuses a concluded test's events.
 *
 * @param options.lessonId - The lesson being tested.
 * @param options.now - Creation time. When omitted, the current time.
 * @return An ID like `lesson-bayes-20261009`, at most 64 characters.
 * @throws Never.
 */
export function newLessonExperimentId({
  lessonId,
  now,
}: {
  lessonId: string;
  now?: Date;
}): string {
  const day = (now ?? new Date()).toISOString().slice(0, 10).replace(/-/g, '');
  const maxLessonLength =
    MAX_EXPERIMENT_ID_LENGTH -
    LESSON_EXPERIMENT_ID_PREFIX.length -
    day.length -
    1;
  return `${LESSON_EXPERIMENT_ID_PREFIX}${lessonId.slice(0, maxLessonLength)}-${day}`;
}

/**
 * Picks the arm name for a new version: the first letter from `a` not
 * already used.
 *
 * @param versions - The test's current versions.
 * @return An unused single-letter id.
 * @throws Never.
 */
export function nextLessonTestVersionId(
  versions: readonly LessonTestVersion[],
): string {
  const used = new Set(versions.map(version => version.id));
  for (let code = 'a'.charCodeAt(0); code <= 'z'.charCodeAt(0); code++) {
    const candidate = String.fromCharCode(code);
    if (!used.has(candidate)) {
      return candidate;
    }
  }
  return `v${String(versions.length + 1)}`;
}

/**
 * Builds the bandit definition of a running lesson test.
 *
 * @param options.courseId - Course whose lesson list holds the lesson.
 * @param options.lessonId - The lesson being tested.
 * @param options.abTest - The lesson's parsed test.
 * @return The definition, or null when the test is off or has fewer than
 *   two versions (then no student is assigned and nothing is recorded).
 * @throws Never.
 */
export function buildLessonExperimentDefinition({
  courseId,
  lessonId,
  abTest,
}: {
  courseId: string;
  lessonId: string;
  abTest: LessonAbTest;
}): BanditExperimentDefinition | null {
  const arms = abTest.versions.map(version => version.id);
  if (
    !abTest.enabled ||
    arms.length < 2 ||
    new Set(arms).size !== arms.length
  ) {
    return null;
  }
  return {
    courseId,
    experimentId: abTest.experimentId,
    description: `Lesson ${lessonId}: ${abTest.versions
      .map(version => version.name || version.id)
      .join(' vs ')}. Reward: completed the lesson within the window.`,
    arms,
    prior: LESSON_TEST_PRIOR,
    rewardWindowHours: LESSON_TEST_REWARD_WINDOW_HOURS,
    cohorts: [DEFAULT_COHORT],
    previewPath: arm =>
      `/${courseId}/learn/${lessonId}?${buildArmOverrideParam({
        experimentId: abTest.experimentId,
        arm,
      })}`,
  };
}

/**
 * Reads which version a slide is assigned to.
 *
 * @param options.abTest - The lesson's parsed test.
 * @param options.slideId - The slide.
 * @return The assigned version id, or null for a slide every student sees.
 * @throws Never.
 */
function assignedVersionId({
  abTest,
  slideId,
}: {
  abTest: LessonAbTest;
  slideId: string;
}): string | null {
  return Object.hasOwn(abTest.slideVersions, slideId)
    ? abTest.slideVersions[slideId]
    : null;
}

/**
 * Reports whether one slide is shown to the students of one version.
 *
 * @param options.abTest - The lesson's parsed test.
 * @param options.slideId - The slide.
 * @param options.versionId - The version the student sees.
 * @return True for a slide everyone sees or one assigned to this version.
 * @throws Never.
 */
export function isSlideShownToVersion({
  abTest,
  slideId,
  versionId,
}: {
  abTest: LessonAbTest;
  slideId: string;
  versionId: string;
}): boolean {
  const assigned = assignedVersionId({abTest, slideId});
  if (assigned === null) {
    return true;
  }
  const exists = abTest.versions.some(version => version.id === assigned);
  return !exists || assigned === versionId;
}

/**
 * Reports whether a slide belongs to one version only. Reaching the first such
 * slide is what counts as exposure to the test.
 *
 * @param options.abTest - The lesson's parsed test.
 * @param options.slideId - The slide.
 * @return True when the slide is assigned to an existing version.
 * @throws Never.
 */
export function isVersionSpecificSlide({
  abTest,
  slideId,
}: {
  abTest: LessonAbTest;
  slideId: string;
}): boolean {
  const assigned = assignedVersionId({abTest, slideId});
  return (
    assigned !== null &&
    abTest.versions.some(version => version.id === assigned)
  );
}
